import loadable from "@loadable/component";
import { inject, observer } from "mobx-react";
import React from "react";
import type { Store } from "../../../client/stores";
import Page from "../../core/components/pages/Page.component";
import { Routes } from "../../core/components/Routes";
import { Title } from "../../core/components/Title.component";
import { Ad } from "../../core/page-blocks/Ad.component";
import { PageBlockRegistry } from "../../core/page-blocks/Block.component";
import { Subscribe } from "../../core/page-blocks/newsletter/Subscribe.component";
import { Unsubscribe } from "../../core/page-blocks/newsletter/Unsubscribe.component";
import { ShowLoader } from "../../core/page-blocks/show/ShowLoader.component";
import { TemplateComponent } from "../TemplateComponent.component";
import { CoastFooter } from "./chrome/CoastFooter.component";
import { CoastHeader } from "./chrome/CoastHeader.component";
import CoastHeaderAd from "./chrome/CoastHeaderAd.component";
import "./index.style.scss";
import CoastItem from "./page-blocks/content/CoastItem.component";
import { DatasourceLoader } from "./page-blocks/datasource/DatasourceLoader.component";
import { CoastGeneric } from "./page-blocks/generic/CoastGeneric.component";
import { Html } from "./page-blocks/html/CoastHtml.component";

const LegalNoticeLoader = loadable(() => import("../../core/page-blocks/legalnotice/LegalNoticeLoader.component"), {
    ssr: false,
});
const LeavingSiteLoader = loadable(() => import("../premiere/components/leaving-site/LeavingSiteLoader.component"), {
    ssr: false,
});

const CoastUserModal = loadable(() => import("./components/user/CoastUserModal.component"), {
    ssr: false,
});

interface TemplateProps {
    store?: Store;
}

const pageComponent = Page;
export const pageComponents = {
    generic_80_20: pageComponent,
    generic_full: pageComponent,
    generic_80_20_with_title: pageComponent,
    detail: pageComponent,
    micro_detail: pageComponent,
    home: pageComponent,
    aggregate_full: pageComponent,
    microhome: pageComponent,
    microhome_rail: pageComponent,
    plaintext: pageComponent,
};

export const blockComponents: PageBlockRegistry = {
    // Bundled
    about: () => loadable(() => import("../../core/components/microsite/MicrositeInfo.component")),
    ad: () => Ad,
    calendar: block =>
        block?.tags?.includes("display-hints/tile")
            ? loadable(() => import("../../core/page-blocks/calendar/CalendarTileLoader.component"))
            : loadable(() => import("./page-blocks/calendar/CoastCalendar.component")),
    contact: () => loadable(() => import("./page-blocks/contact/CoastContact.component")),
    contest: () => loadable(() => import("../../core/page-blocks/contest/ContestLoader.component")),
    contests: block =>
        block?.tags?.includes("display-hints/tile")
            ? loadable(() => import("../../core/page-blocks/contests/ContestsTileLoader.component"), { ssr: false })
            : loadable(() => import("../../core/page-blocks/contests/ContestsLoader.component")),
    event: () => loadable(() => import("./page-blocks/event/CoastEventDetailLoader.component")),
    feed: () => DatasourceLoader,
    header: () => loadable(() => import("../../core/page-blocks/header/Header.component")),
    html: () => Html,
    item: () => CoastItem,
    "item-reference": () => loadable(() => import("./page-blocks/item-reference/CoastItemReference.component")),
    newslettersubscribe: () => Subscribe,
    newsletterunsubscribe: () => Unsubscribe,
    onair: () => loadable(() => import("../../core/components/tiles/OnairTileLoader.component")),
    onairschedule: () => loadable(() => import("../../core/components/onair/OnAirSchedule.component")), // This is for the /schedule page, so you don't get confused
    playlist: block =>
        block?.tags?.includes("display-hints/tile")
            ? loadable(() => import("../../core/page-blocks/music/MusicTile.component"))
            : loadable(() => import("../../core/page-blocks/music/MusicPage.component")),
    podcasts: () => loadable(() => import("../../core/page-blocks/podcast/PodcastsLoader.component")),
    recommendation: () =>
        loadable(() => import("./page-blocks/recommendation/CoastRecommendation.component"), { ssr: false }),
    search: () => loadable(() => import("./page-blocks/search/SearchLoader.component")),
    show: () => ShowLoader,
    title: () => Title,
    topsongs: () => loadable(() => import("../../core/page-blocks/music/MusicPage.component")),
    traffic: block =>
        block?.tags?.includes("display-hints/tile")
            ? loadable(() => import("../../core/components/tiles/TrafficTile.component"))
            : loadable(() => import("../../core/page-blocks/traffic/Traffic.component")),
    weather: block =>
        block?.tags?.includes("display-hints/tile")
            ? loadable(() => import("../../core/components/tiles/WeatherTileLoader.component"))
            : loadable(() => import("../../core/page-blocks/weather/WeatherLoader.component")),

    asset: () => loadable(() => import("../../core/page-blocks/asset/Asset.component")),
    instagram: () => () => null,
    twitter: () => loadable(() => import("../../core/page-blocks/twitter/Twitter.component")),
    howToListen: () =>
        loadable(() => import("../../core/page-blocks/howToListen/HowToListenLoader.component"), {
            ssr: false,
        }),
    fcc: () => loadable(() => import("../../core/page-blocks/fccApplication/fccApplication.component")),
    video: () => loadable(() => import("../../sites/coast/components/coast-media/CoastMediaLoader.component")),
    videoPlaylist: () =>
        loadable(() => import("../../sites/coast/components/coast-media/CoastMediaPlaylistLoader.component")),
    generic: () => CoastGeneric,
};

export const CoastTemplate = inject("store")(
    observer(({ store }: TemplateProps) => {
        if (!store) {
            return null;
        }

        return (
            <TemplateComponent>
                {store.page.tagsSet.has("display-hints/omit-header") ? null : <CoastHeader />}
                <CoastHeaderAd />
                <Routes pageComponents={pageComponents} blockComponents={blockComponents} />
                <LeavingSiteLoader />
                <CoastFooter />
                <LegalNoticeLoader />
                <CoastUserModal />
            </TemplateComponent>
        );
    }),
);

export default CoastTemplate;
